
import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Product } from "@/types";
import ProductForm from "./products/ProductForm";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface EditProductSheetProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedProduct: Product) => void;
}

const EditProductSheet = ({ product, isOpen, onClose, onSave }: EditProductSheetProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [connectionError, setConnectionError] = useState(false);

  useEffect(() => {
    // Check connection when sheet is opened
    const checkConnection = async () => {
      if (isOpen) {
        try {
          const { error } = await supabase
            .from('products')
            .select('count', { count: 'exact', head: true });
          
          setConnectionError(!!error);
          
          if (error) {
            console.error('Erreur de connexion Supabase:', error);
          }
        } catch (err) {
          console.error('Échec de la vérification de la connexion:', err);
          setConnectionError(true);
        }
      }
    };
    
    checkConnection();
  }, [isOpen]);

  const handleSave = async (updatedProduct: Product) => {
    if (!product) return;
    
    setIsSubmitting(true);
    try {
      await onSave(updatedProduct as Product);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="overflow-y-auto max-h-screen">
        <SheetHeader>
          <SheetTitle>Modifier le produit</SheetTitle>
          <SheetDescription>
            Modifiez les informations du produit ci-dessous
          </SheetDescription>
        </SheetHeader>

        {connectionError && (
          <Alert className="my-4 border-destructive bg-destructive/10">
            <AlertDescription className="text-destructive">
              Problème de connexion à la base de données. Certaines fonctionnalités peuvent ne pas fonctionner correctement.
            </AlertDescription>
          </Alert>
        )}

        <ProductForm 
          product={product}
          onSave={handleSave}
          isSubmitting={isSubmitting || connectionError}
          onClose={onClose}
        />
      </SheetContent>
    </Sheet>
  );
};

export default EditProductSheet;
